import { useEffect, useState } from 'react'
import Header from '@/components/layout/Header'
import { useAuth, isOperator as hasOperatorRole } from '@/contexts/AuthContext'
import { Plus, Trash2, Pencil, X, Check } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import MatchClassSettings from '@/components/settings/MatchClassSettings'
import RosterDefaultSettings from '@/components/settings/RosterDefaultSettings'
import RosterCatalogSettings from '@/components/settings/RosterCatalogSettings'
import SettingsSection, {
  SettingsEmpty, SettingsSkeleton,
  settingsAddButton, settingsAddRow, settingsIconButton, settingsInput, settingsRow,
} from '@/components/settings/SettingsSection'

interface SquadPreset {
  id: number
  name: string
  color: string
}

interface VehiclePreset {
  id: number
  name: string
  speed: number
}


export default function SettingsPage() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const isOperator = hasOperatorRole(user)

  const [presets, setPresets] = useState<SquadPreset[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState('#3b82f6')
  const [creating, setCreating] = useState(false)

  const [vehicles, setVehicles] = useState<VehiclePreset[]>([])
  const [vLoading, setVLoading] = useState(true)
  const [vEditingId, setVEditingId] = useState<number | null>(null)
  const [vEditName, setVEditName] = useState('')
  const [vEditSpeed, setVEditSpeed] = useState(0)
  const [vNewName, setVNewName] = useState('')
  const [vNewSpeed, setVNewSpeed] = useState<number | ''>('')
  const [vCreating, setVCreating] = useState(false)

  const fetchPresets = () => {
    setLoading(true)
    fetch('/api/squad-presets', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setPresets)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const fetchVehicles = () => {
    setVLoading(true)
    fetch('/api/vehicle-presets', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setVehicles)
      .catch(() => {})
      .finally(() => setVLoading(false))
  }

  useEffect(() => { fetchPresets(); fetchVehicles() }, [])

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const res = await fetch('/api/squad-presets', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), color: newColor }),
      })
      if (res.ok) {
        setNewName('')
        setNewColor('#3b82f6')
        fetchPresets()
      }
    } catch { /* ignore */ }
    setCreating(false)
  }

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return
    try {
      const res = await fetch(`/api/squad-presets/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim(), color: editColor }),
      })
      if (res.ok) {
        setEditingId(null)
        fetchPresets()
      }
    } catch { /* ignore */ }
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/squad-presets/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (res.ok) fetchPresets()
    } catch { /* ignore */ }
  }

  const startEdit = (p: SquadPreset) => {
    setEditingId(p.id)
    setEditName(p.name)
    setEditColor(p.color)
  }

  const handleVCreate = async () => {
    if (!vNewName.trim()) return
    setVCreating(true)
    try {
      const res = await fetch('/api/vehicle-presets', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: vNewName.trim(), speed: Number(vNewSpeed) || 0 }),
      })
      if (res.ok) {
        setVNewName('')
        setVNewSpeed('')
        fetchVehicles()
      }
    } catch { /* ignore */ }
    setVCreating(false)
  }

  const handleVUpdate = async (id: number) => {
    if (!vEditName.trim()) return
    try {
      const res = await fetch(`/api/vehicle-presets/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: vEditName.trim(), speed: Number(vEditSpeed) || 0 }),
      })
      if (res.ok) {
        setVEditingId(null)
        fetchVehicles()
      }
    } catch { /* ignore */ }
  }

  const handleVDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/vehicle-presets/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (res.ok) fetchVehicles()
    } catch { /* ignore */ }
  }

  const startVEdit = (v: VehiclePreset) => {
    setVEditingId(v.id)
    setVEditName(v.name)
    setVEditSpeed(v.speed)
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.settings.title} />
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-3xl space-y-5">

          {/* ── Squad presets ──────────────────────────────────────────────── */}
          <SettingsSection title={T.settings.squadPresets} description={T.settings.squadPresetsDesc}>
            {loading ? <SettingsSkeleton /> : (
              <div className="space-y-2">
                {presets.map(p => (
                  <div key={p.id} className={settingsRow}>
                    {editingId === p.id ? (
                      <>
                        <input
                          type="color"
                          value={editColor}
                          onChange={e => setEditColor(e.target.value)}
                          className="h-8 w-8 shrink-0 cursor-pointer rounded border-0"
                        />
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') setEditingId(null) }}
                          className={`${settingsInput} min-w-0 flex-1`}
                          autoFocus
                        />
                        <button onClick={() => handleUpdate(p.id)} className={`${settingsIconButton} hover:text-green-400`}>
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className={settingsIconButton}>
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          className="h-6 w-6 shrink-0 rounded-sm border border-white/20"
                          style={{ backgroundColor: p.color }}
                        />
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{p.name}</span>
                        {isOperator && (
                          <>
                            <button onClick={() => startEdit(p)} className={settingsIconButton}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleDelete(p.id)} className={`${settingsIconButton} hover:text-destructive`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {presets.length === 0 && <SettingsEmpty>{T.settings.noSquadPresets}</SettingsEmpty>}
              </div>
            )}

            {isOperator && (
              <div className={settingsAddRow}>
                <input
                  type="color"
                  value={newColor}
                  onChange={e => setNewColor(e.target.value)}
                  className="h-8 w-8 shrink-0 cursor-pointer rounded border-0"
                />
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
                  placeholder={T.settings.newSquadName}
                  className={`${settingsInput} min-w-0 flex-1`}
                />
                <button onClick={handleCreate} disabled={!newName.trim() || creating} className={settingsAddButton}>
                  <Plus className="h-3.5 w-3.5" />
                  {creating ? '...' : T.settings.add}
                </button>
              </div>
            )}
          </SettingsSection>

          {/* ── Vehicle presets ────────────────────────────────────────────── */}
          <SettingsSection title={T.settings.vehiclePresets} description={T.settings.vehiclePresetsDesc}>
            {vLoading ? <SettingsSkeleton /> : (
              <div className="space-y-2">
                {vehicles.map(v => (
                  <div key={v.id} className={settingsRow}>
                    {vEditingId === v.id ? (
                      <>
                        <input
                          type="text"
                          value={vEditName}
                          onChange={e => setVEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleVUpdate(v.id); if (e.key === 'Escape') setVEditingId(null) }}
                          className={`${settingsInput} min-w-0 flex-1`}
                          autoFocus
                        />
                        <div className="flex shrink-0 items-center gap-1">
                          <input
                            type="number"
                            step="1"
                            value={vEditSpeed}
                            onChange={e => setVEditSpeed(Number(e.target.value))}
                            className={`${settingsInput} w-20`}
                          />
                          <span className="text-[10px] text-muted-foreground">km/h</span>
                        </div>
                        <button onClick={() => handleVUpdate(v.id)} className={`${settingsIconButton} hover:text-green-400`}>
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setVEditingId(null)} className={settingsIconButton}>
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{v.name}</span>
                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{v.speed} km/h</span>
                        {isOperator && (
                          <>
                            <button onClick={() => startVEdit(v)} className={settingsIconButton}>
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => handleVDelete(v.id)} className={`${settingsIconButton} hover:text-destructive`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {vehicles.length === 0 && <SettingsEmpty>{T.settings.noVehiclePresets}</SettingsEmpty>}
              </div>
            )}

            {isOperator && (
              <div className={settingsAddRow}>
                <input
                  type="text"
                  value={vNewName}
                  onChange={e => setVNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleVCreate() }}
                  placeholder={T.settings.newVehicleName}
                  className={`${settingsInput} min-w-0 flex-1`}
                />
                <div className="flex shrink-0 items-center gap-1">
                  <input
                    type="number"
                    step="1"
                    value={vNewSpeed}
                    onChange={e => setVNewSpeed(e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder={T.settings.speed}
                    className={`${settingsInput} w-24`}
                  />
                  <span className="text-[10px] text-muted-foreground">km/h</span>
                </div>
                <button onClick={handleVCreate} disabled={!vNewName.trim() || vCreating} className={settingsAddButton}>
                  <Plus className="h-3.5 w-3.5" />
                  {vCreating ? '...' : T.settings.add}
                </button>
              </div>
            )}
          </SettingsSection>

          {/* ── Lineup (match sign-up positions) ───────────────────────────── */}
          <MatchClassSettings />

          {/* ── Roster roles + squad types (what rosters are built from) ───── */}
          <RosterCatalogSettings />

          {/* ── Default roster (what a match roster opens with) ────────────── */}
          <RosterDefaultSettings />

        </div>
      </div>
    </div>
  )
}
